import React from 'react';

import { css, cx } from '@emotion/css';
import { Card, CardActions, CardContent, CardHeader } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/assets/icons.ts';
import { UsePatientNoteReturn } from '@/hooks/usePatientNote.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Queries } from '@/queries/Queries.ts';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { RichTextEditor } from '@/uiKit/molecules/RichText/RichTextEditor.tsx';
import { RichTextViewer } from '@/uiKit/molecules/RichText/RichTextViewer.tsx';

export type PatientNoteProps = {
  className?: string;
  note: UsePatientNoteReturn;
  classes?: {
    root?: string;
    header?: string;
    content?: string;
    footer?: string;
  };
};

export const PatientNote: React.FC<PatientNoteProps> = ({
  className,
  classes,
  note,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { data: featureFlags } = Queries.user.useFeatureFlags({});

  if (!featureFlags?.['medical-team-notes']) {
    return null;
  }

  return (
    <Card className={cx(styles.container, className, classes?.root)}>
      <CardHeader
        title={t('pages.patientMedicalFile.notes')}
        className={cx(styles.header, classes?.header)}
      />
      {note.editing && (
        <CardContent className={cx(styles.content, classes?.content)}>
          <RichTextEditor content={note.content} onChange={note.editNote} />
        </CardContent>
      )}
      {!note.editing && !note.isNewNote && (
        <CardContent className={cx(styles.content, classes?.content)}>
          <RichTextViewer content={note.content} />
        </CardContent>
      )}
      <CardActions className={cx(styles.footer, classes?.footer)}>
        {!note.editing && note.isNewNote && (
          <Button variant="text" startIcon={<Icons.plus />} onClick={note.edit}>
            {t('pages.patientMedicalFile.writeNote')}
          </Button>
        )}
        {!note.editing && !note.isNewNote && (
          <Button variant="text" startIcon={<Icons.edit />} onClick={note.edit}>
            {t('pages.patientMedicalFile.editNote')}
          </Button>
        )}
        {note.editing && (
          <Button variant="contained" onClick={note.cancel}>
            {t('button.cancel')}
          </Button>
        )}
        {note.editing && (
          <Button variant="contained" onClick={note.save}>
            {t('button.save')}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const makeStyles = () => ({
  container: css({
    minWidth: 0,
  }),
  header: css({
    flex: '0',
  }),
  content: css({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'auto',
    minWidth: 0,
    maxWidth: '100%',
    scrollbarColor: 'lightgrey transparent',
    '::-webkit-scrollbar': {
      backgroundColor: 'transparent',
    },
  }),

  footer: css({
    flex: '0',
  }),
});
